import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import ProductCard from "../components/ProductCard";
import TrustBadges from "../components/TrustBadges";
import SEO, { buildOrganizationSchema, buildWebsiteSchema } from "../components/SEO";
import { Shield, Truck, RefreshCw, Headphones, ChevronRight } from "lucide-react";

const HomePage = () => {
  const { t, language, products } = useApp();
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const bestSellers = products.filter((p) => p.is_best_seller).slice(0, 4);
  const newArrivals = products.slice(0, 8);
  const isDE = language === "de";

  // Hero image: pick a beautiful lifestyle/product shot from product 5 (Signature Heart) banner
  const HERO_IMG = "/products/05_Signature-Heart-Bracelet_87722-0/87722-0_v2_lifestyle.jpg";
  const HERO_FALLBACK = "/products/01_Flower-Bracelet_87755-0/banniere.webp";

  const subscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribed(true);
    setEmail("");
  };

  return (
    <main data-testid="home-page">
      <SEO
        title={isDE ? "Luxusschmuck — Wasserfest · 18K Vergoldet · Hypoallergen" : "Luxury Waterproof Jewelry · 18K Gold Plated · Hypoallergenic"}
        description={isDE
          ? "LUXERA KaLiMa — Eleganter Luxusschmuck zum Strahlen jeden Tag. Wasserfest, hypoallergen, 18K vergoldet. Kostenloser Versand in Deutschland. 180 Tage Garantie."
          : "LUXERA KaLiMa — Elegant luxury jewelry designed to shine every day. Waterproof, hypoallergenic, 18K gold plated. Free shipping in Germany. 180-day warranty."}
        canonical={isDE ? "/?lang=de" : "/"}
        ogImage={products[0]?.images?.[0]}
        hreflangs={{ en: "/", de: "/?lang=de" }}
        jsonLd={[
          buildOrganizationSchema(typeof window !== "undefined" ? window.location.origin : ""),
          buildWebsiteSchema(typeof window !== "undefined" ? window.location.origin : ""),
        ]}
      />
      {/* HERO */}
      <section className="relative h-[90vh] min-h-[640px] overflow-hidden grain" data-testid="hero">
        <img src={HERO_IMG} alt="LUXERA KaLiMa" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.target.src = HERO_FALLBACK; }} />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative h-full max-w-[1500px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-20 lg:pb-28">
          <div className="max-w-2xl">
            <div className="text-[11px] tracking-[0.4em] uppercase text-[var(--gold)] mb-6 reveal">— {isDE ? "Neue Kollektion 2026" : "New Collection 2026"}</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.02] text-white reveal-2" data-testid="hero-title">
              {t.hero.title}
            </h1>
            <p className="text-base lg:text-lg text-white/85 mt-7 tracking-[0.2em] uppercase reveal-3">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-wrap gap-3 mt-10 reveal-4">
              <Link to="/shop" className="btn-lux bg-[var(--ivory)] text-[var(--noir)] hover:text-[var(--gold)] px-9 py-4 text-[11px] tracking-[0.3em] uppercase" data-testid="hero-cta1">
                {t.hero.cta1}
              </Link>
              <Link to="/best-sellers" className="border border-white text-white px-9 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-white hover:text-[var(--noir)] transition-colors" data-testid="hero-cta2">
                {t.hero.cta2}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Best Sellers */}
      <section className="py-24 lg:py-32 bg-[var(--ivory)]" data-testid="best-sellers-section">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col items-center text-center mb-16">
            <div className="text-[11px] tracking-[0.4em] uppercase text-[var(--gold-deep)] mb-4">★ Bestsellers</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[var(--noir)]">{t.sections.bestSellers}</h2>
            <p className="text-sm text-[var(--grey)] mt-4 tracking-wide">{t.sections.bestSellersSub}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {bestSellers.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
          <div className="text-center mt-12">
            <Link to="/shop" className="link-lux text-xs tracking-[0.3em] uppercase text-[var(--noir)] inline-flex items-center gap-2">
              {isDE ? "Alle Produkte ansehen" : "View All Products"} <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      <TrustBadges />

      {/* Editorial / Story */}
      <section className="py-24 lg:py-32 bg-white" data-testid="story-section">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="aspect-[4/5] overflow-hidden order-2 lg:order-1">
            <img src="/products/03_Flower-Bracelet-Perlmutt_87917-0/banniere.webp" alt="" className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="order-1 lg:order-2">
            <div className="text-[11px] tracking-[0.4em] uppercase text-[var(--gold-deep)] mb-5">— {isDE ? "Die KaLiMa Geschichte" : "The KaLiMa Story"}</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">
              {isDE ? "Schmuck, der mit dir lebt." : "Jewelry that lives with you."}
            </h2>
            <p className="text-[15px] leading-relaxed text-[var(--noir-soft)] mb-6">
              {isDE
                ? "LUXERA KaLiMa wurde aus einer einfachen Überzeugung geboren: Jede Frau verdient eleganten Schmuck, der keine Kompromisse macht. Stücke, die sich am ersten Tag genauso edel anfühlen wie in fünf Jahren — wasserfest, hautfreundlich, zeitlos."
                : "LUXERA KaLiMa was born from a simple belief: every woman deserves elegant jewelry that doesn't compromise. Pieces that feel as precious on day one as they will in five years — waterproof, skin-friendly, timeless."}
            </p>
            <p className="text-[15px] leading-relaxed text-[var(--noir-soft)] mb-8">
              {isDE
                ? "18K-Gold-PVD auf einer Titan-Bindeschicht (nickelfrei). Edelstahl 316. Mit Liebe in Thailand gefertigt. 180 Tage Garantie."
                : "18K PVD gold over a titanium bonding layer (nickel-free). 316 stainless steel. Made with love in Thailand. 180-day warranty."}
            </p>
            <Link to="/about" className="link-lux text-xs tracking-[0.25em] uppercase">{isDE ? "Mehr erfahren" : "Discover more"} →</Link>
          </div>
        </div>
      </section>

      {/* Shop the collection grid */}
      <section className="py-24 lg:py-28 bg-[var(--ivory)]" data-testid="collection-section">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 gap-6">
            <div>
              <div className="text-[11px] tracking-[0.4em] uppercase text-[var(--gold-deep)] mb-3">— {isDE ? "Die Kollektion" : "The Collection"}</div>
              <h2 className="font-display text-4xl md:text-5xl">{isDE ? "Stücke, die du jeden Tag tragen möchtest" : "Pieces You'll Want To Wear Every Day"}</h2>
            </div>
            <Link to="/shop" className="link-lux text-xs tracking-[0.25em] uppercase whitespace-nowrap">{isDE ? "Alle Stücke" : "View All"} →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
            {newArrivals.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-24 lg:py-32 bg-[var(--blush)]/40" data-testid="reviews-section">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="text-[11px] tracking-[0.4em] uppercase text-[var(--gold-deep)] mb-4">★★★★★</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl">{t.sections.reviews}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10">
            {[
              { name: "Sophia M.", body: isDE ? "Die Verarbeitung ist unglaublich. Nach einem Monat täglichen Tragens — sogar in der Dusche — sieht es noch immer aus wie neu." : "Craftsmanship is incredible. Still looks brand new after a month of daily wear, even in the shower." },
              { name: "Emma L.", body: isDE ? "Leicht, elegant und läuft nicht an. Ich bekomme jeden Tag Komplimente." : "Lightweight, elegant, doesn't tarnish. I get compliments every single day." },
              { name: "Charlotte K.", body: isDE ? "Die Verpackung allein fühlt sich luxuriös an. Die Kristalle fangen das Licht wunderschön ein." : "The packaging alone feels luxurious. The crystals catch the light beautifully." },
            ].map((r, i) => (
              <figure key={i} className="bg-white p-8 lg:p-10 reveal" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="text-[var(--gold)] mb-5">{"★".repeat(5)}</div>
                <blockquote className="font-serif text-lg leading-relaxed text-[var(--noir)] mb-6">"{r.body}"</blockquote>
                <figcaption className="text-[11px] tracking-[0.25em] uppercase text-[var(--grey)]">— {r.name} · {isDE ? "Verifiziert" : "Verified"}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantees */}
      <section className="py-20 bg-white border-y border-[var(--champagne)]/40" data-testid="guarantees-section">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { Icon: Truck, label: isDE ? "Kostenloser Versand DE" : "Free Shipping DE" },
            { Icon: RefreshCw, label: isDE ? "30 Tage Rückgabe" : "30-Day Returns" },
            { Icon: Shield, label: isDE ? "180 Tage Garantie" : "180-Day Warranty" },
            { Icon: Headphones, label: isDE ? "Support 7/7" : "Support 7/7" },
          ].map(({ Icon, label }, i) => (
            <div key={i} className="flex flex-col items-center text-center">
              <Icon className="w-8 h-8 text-[var(--gold-deep)] mb-3" strokeWidth={1.25} />
              <div className="text-[11px] tracking-[0.25em] uppercase">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-24 lg:py-32 bg-[var(--noir)] text-[var(--ivory)] grain" data-testid="newsletter-section">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="text-[11px] tracking-[0.4em] uppercase text-[var(--gold)] mb-5">✦ {isDE ? "Exklusives Angebot" : "Exclusive Offer"}</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mb-6">{t.sections.newsletter}</h2>
          <p className="text-base text-white/70 mb-10 leading-relaxed">{t.sections.newsletterSub}</p>
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="home-newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.sections.newsletterPh}
              className="flex-1 bg-transparent border border-white/30 px-5 py-4 text-sm placeholder:text-white/50 focus:border-[var(--gold)] outline-none"
              data-testid="home-newsletter-email"
            />
            <button className="btn-lux bg-[var(--gold)] text-[var(--noir)] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:text-[var(--ivory)]" data-testid="home-newsletter-submit">
              {t.sections.newsletterBtn}
            </button>
          </form>
          {subscribed && <p className="mt-6 text-sm text-[var(--gold)]" data-testid="home-newsletter-success">✦ {isDE ? "Willkommen!" : "Welcome!"} {isDE ? "Dein Code" : "Your code"}: <strong>WELCOME10</strong></p>}
        </div>
      </section>

      {/* UGC */}
      <section className="py-24 lg:py-28" data-testid="ugc-section">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 mb-12 text-center">
          <div className="text-[11px] tracking-[0.4em] uppercase text-[var(--gold-deep)] mb-3">{t.sections.ugcSub}</div>
          <h2 className="font-display text-4xl md:text-5xl">{t.sections.ugc}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
          {products.slice(0, 6).map((p, i) => (
            <Link to={`/product/${p.slug}`} key={i} className="aspect-square overflow-hidden img-zoom bg-[var(--ivory-deep)] block">
              <img src={p.images[1] || p.images[0]} alt={p.name_en} className="w-full h-full object-cover" loading="lazy" />
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
};

export default HomePage;
